import type { OverflowWindowInfo } from '@/types/api'

export interface TimeRemaining {
  hours: number
  minutes: number
  seconds: number
}

/**
 * The next daily reset.
 *
 * Offers do NOT share a reset any more: each rolls over at 00:00 in its own timezone
 * (cap-api `offers.reset_timezone`), so Reco resets at midnight Pacific while everything
 * else resets at midnight UTC. `nextReset` is the server's answer for the offer currently
 * on screen — `overflow_window.resets_at_utc` from the dashboard payload — and callers
 * should pass it wherever they have it.
 *
 * The no-argument form falls back to midnight UTC. That is right for every UTC offer and
 * for the admin pages, and it is only ever a fallback: a Pacific offer showing a UTC
 * countdown means the payload had not landed yet, not that the reset moved.
 */
export function getNextResetUTC(nextReset?: string | Date | null): Date {
  if (nextReset) {
    const parsed = nextReset instanceof Date ? nextReset : new Date(nextReset)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }

  const now = new Date()
  const todayMidnightUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0)
  )

  // If we've already passed today's midnight UTC, target tomorrow
  if (now.getTime() >= todayMidnightUTC.getTime()) {
    return new Date(todayMidnightUTC.getTime() + 86_400_000)
  }
  return todayMidnightUTC
}

export function getTimeRemaining(targetUTC: Date): TimeRemaining {
  const diff = Math.max(0, targetUTC.getTime() - Date.now())
  const totalSeconds = Math.floor(diff / 1000)
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  }
}

/**
 * `HH:MM:SS` countdown, hours uncapped — a 26-hour gap renders as "26:00:00", never "02:00:00".
 *
 * Mirrors admin-dashboard's `formatCountdown` in `lib/reset-window.ts`, so the same remaining time
 * reads identically on both dashboards. `getTimeRemaining()` is the parts form of the same thing;
 * use this when you want the rendered string.
 */
export function formatCountdown(ms: number): string {
  const total = Math.floor(Math.max(0, ms) / 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`
}

export function msUntilReset(nextReset?: string | Date | null, now = new Date()): number {
  return Math.max(0, getNextResetUTC(nextReset).getTime() - now.getTime())
}

/** True when within `hours` of the next daily reset for the offer on screen. */
export function isWithinHoursOfReset(
  hours: number,
  nextReset?: string | Date | null,
  now = new Date()
): boolean {
  return msUntilReset(nextReset, now) <= hours * 3600_000
}

/**
 * Matches cap-api `overflowPriorityWindow()` — priority window retired, overflow opens at
 * the offer's own reset. Client-side fallback only, for the moments before the server's
 * window lands; pass `nextReset` when the offer's reset instant is known.
 */
export function computeOverflowWindowClient(
  now = new Date(),
  nextReset?: string | Date | null
): OverflowWindowInfo {
  const PRIORITY_WINDOW_HOURS = 0
  const reset = getNextResetUTC(nextReset)
  const resetAnchor = new Date(reset.getTime() - 86_400_000)
  const hoursSinceReset = (now.getTime() - resetAnchor.getTime()) / 3600_000
  const isPriorityWindow = PRIORITY_WINDOW_HOURS > 0 && hoursSinceReset >= 0 && hoursSinceReset < PRIORITY_WINDOW_HOURS
  const opensAt = new Date(resetAnchor.getTime() + PRIORITY_WINDOW_HOURS * 3600_000)
  return {
    is_priority_window: isPriorityWindow,
    overflow_locked: isPriorityWindow,
    opens_at_utc: opensAt.toISOString(),
    resets_at_utc: reset.toISOString(),
    now_utc: now.toISOString(),
  }
}

export function formatLocalTime(utcDate: Date): string {
  return utcDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}
